import { formatPesoAmount } from '../utils/money';
import React, { useEffect, useMemo, useState } from 'react';
import InlineLoading from './InlineLoading';

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const PosSessionModal = ({
  isOpen,
  mode = 'open',
  registers = EMPTY_ARRAY,
  registersByBranch = EMPTY_OBJECT,
  categoriesByBranch = EMPTY_OBJECT,
  branches = EMPTY_ARRAY,
  activeBranchId,
  loading = false,
  error,
  activeRegisterId,
  expectedCash,
  expectedCashLoading = false,
  expectedCashError,
  openSales = EMPTY_ARRAY,
  openSalesLoading = false,
  openSalesError,
  onOpenSale,
  onCancelSale,
  onSelectSession,
  onSelectRegisterless,
  onOpenSession,
  onCloseSession,
  onClose,
  currentUserId,
  canOpenSession = true,
  canCloseSession = true,
  canOpenSale = true,
  canCancelSale = true,
  canManageRegisters = false,
  onGoToRegisterSettings,
  onGoHome,
  registerlessProfile = null,
  registerlessAvailable = false,
  openingSession = false,
  closingSession = false,
  closeTitle = 'Cerrar caja',
  closeDescription = '',
  closeReasonRequired = false,
  closeReasonLabel = '',
}) => {
  const [selectedId, setSelectedId] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [openingCash, setOpeningCash] = useState('');
  const [closingCash, setClosingCash] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const formatCurrency = (value) => `$${formatPesoAmount(Number(value || 0))}`;

  useEffect(() => {
    if (!isOpen) return;
    const nextBranchId =
      activeBranchId
      || branches[0]?.id
      || Object.keys(registersByBranch || {})[0]
      || '';
    setSelectedBranchId(nextBranchId ? String(nextBranchId) : '');
    setOpeningCash('');
    setClosingCash('');
    setCloseReason('');
  }, [isOpen, activeBranchId, branches, registersByBranch]);

  const branchRegisters = useMemo(() => {
    if (selectedBranchId && registersByBranch) {
      const direct = registersByBranch[selectedBranchId];
      if (direct) return direct;
      const numericKey = Number(selectedBranchId);
      if (Number.isInteger(numericKey) && registersByBranch[numericKey]) {
        return registersByBranch[numericKey];
      }
    }
    return registers;
  }, [registers, registersByBranch, selectedBranchId]);

  useEffect(() => {
    if (!isOpen) return;
    const hasActiveInBranch = branchRegisters.some(
      (register) => String(register.id) === String(activeRegisterId)
    );
    const nextId = hasActiveInBranch
      ? activeRegisterId
      : branchRegisters[0]?.id || registers[0]?.id || '';
    setSelectedId(nextId ? String(nextId) : '');
  }, [isOpen, activeRegisterId, branchRegisters, registers]);

  const selectedRegister = useMemo(
    () => branchRegisters.find((register) => String(register.id) === String(selectedId)),
    [branchRegisters, selectedId]
  );
  const activeSession = selectedRegister?.activeSession || null;
  const sessionOwnerId = activeSession?.openedBy;
  const isSessionOwner = activeSession && currentUserId
    ? String(sessionOwnerId) === String(currentUserId)
    : false;
  const sessionOwnerLabel = activeSession?.OpenedByUser?.name
    || activeSession?.OpenedByUser?.username
    || (sessionOwnerId ? `Usuario #${sessionOwnerId}` : 'otro usuario');
  const isSessionLocked = Boolean(activeSession) && !isSessionOwner;
  const parsedClosingCash = closingCash === '' ? Number.NaN : Number(closingCash);
  const closingCashValue = Number.isFinite(parsedClosingCash) ? parsedClosingCash : null;
  const expectedCashValue = Number.isFinite(Number(expectedCash)) ? Number(expectedCash) : null;
  const cashDifference = closingCashValue !== null && expectedCashValue !== null
    ? closingCashValue - expectedCashValue
    : null;
  const requiresReason = closeReasonRequired || (cashDifference !== null && cashDifference !== 0);
  const missingReason = requiresReason && !closeReason.trim();
  const hasOpenSales = Array.isArray(openSales) && openSales.length > 0;
  const differenceClassName = cashDifference === null
    ? 'text-gray-700'
    : cashDifference === 0
      ? 'text-green-600'
      : 'text-red-600';

  if (!isOpen) return null;

  const showBranchSidebar = Array.isArray(branches) && branches.length > 1;
  const resolvedBranchId = selectedBranchId || String(activeBranchId || '');
  const branchCategories = (() => {
    if (!resolvedBranchId || !categoriesByBranch) return [];
    const direct = categoriesByBranch[resolvedBranchId];
    if (direct) return direct;
    const numericKey = Number(resolvedBranchId);
    if (Number.isInteger(numericKey) && categoriesByBranch[numericKey]) {
      return categoriesByBranch[numericKey];
    }
    return [];
  })();

  const resolveRegisterCategories = (register) => {
    if (!register || !resolvedBranchId) return [];
    return (branchCategories || []).filter((category) => {
      if (category?.isActive === false) return false;
      const rows =
        category?.CategoryAvailabilities
        || category?.CategoryAvailability
        || [];
      return rows.some((row) => {
        if (row?.isActive === false) return false;
        if (String(row.branchId) !== String(resolvedBranchId)) return false;
        if (!row.cashRegisterId) return true;
        return String(row.cashRegisterId) === String(register.id);
      });
    });
  };

  const renderRegisterStatus = (register) => {
    if (!register?.activeSession) {
      return { label: 'Disponible', className: 'text-green-700 bg-green-50 border-green-200' };
    }
    const openedBy = String(register.activeSession.openedBy || '');
    if (openedBy && String(openedBy) === String(currentUserId)) {
      return { label: 'Abierta por ti', className: 'text-blue-700 bg-blue-50 border-blue-200' };
    }
    return { label: 'Ocupada', className: 'text-red-700 bg-red-50 border-red-200' };
  };

  const canShowRegisterless =
    registerlessAvailable
    && registerlessProfile
    && (!activeBranchId || String(resolvedBranchId) === String(activeBranchId));

  const renderOpen = () => (
    <>
      <h2 className="text-lg font-semibold mb-3">Abrir caja</h2>
      <div className={`flex flex-col gap-4 ${showBranchSidebar ? 'lg:flex-row' : ''}`}>
        {showBranchSidebar && (
          <aside className="w-full border-b border-gray-200 pb-3 lg:w-52 lg:border-b-0 lg:border-r lg:pb-0 lg:pr-3">
            <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Sucursales</div>
            <div className="flex gap-2 overflow-x-auto pb-1 lg:max-h-[420px] lg:flex-col lg:overflow-x-hidden lg:overflow-y-auto lg:pr-1">
              {branches.map((branch) => {
                const isActive = String(branch.id) === String(resolvedBranchId);
                const branchRegistersCount =
                  registersByBranch?.[branch.id]?.length
                  ?? registersByBranch?.[String(branch.id)]?.length
                  ?? 0;
                return (
                  <button
                    key={branch.id}
                    type="button"
                    className={`min-w-[11rem] px-3 py-2 rounded-lg border text-left lg:min-w-0 ${
                      isActive
                        ? 'border-blue-200 bg-blue-50 text-blue-700'
                        : 'border-transparent hover:border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedBranchId(String(branch.id))}
                  >
                    <div className="truncate text-sm font-semibold">{branch.branchName || `Sucursal ${branch.id}`}</div>
                    <div className="text-xs text-gray-500">{branchRegistersCount} cajas</div>
                  </button>
                );
              })}
            </div>
          </aside>
        )}
        <div className="flex-1 min-w-0">
          {loading && (
            <div className="text-sm text-gray-600 mb-4">
              <InlineLoading label="Cargando cajas" />
            </div>
          )}
          {error && !loading && (
            <div className="text-sm text-red-600 mb-4">{error}</div>
          )}
          {!loading && !error && branchRegisters.length === 0 && !canShowRegisterless ? (
            <>
              <p className="text-sm text-gray-600">
                {canManageRegisters
                  ? 'No hay cajas creadas en esta sucursal. Configura una para poder abrir punto de venta.'
                  : 'No hay cajas creadas en esta sucursal. Solicita al encargado que agregue una caja.'}
              </p>
              <div className="flex flex-col-reverse gap-2 mt-4 sm:flex-row sm:justify-end">
                {canManageRegisters ? (
                  <button
                    type="button"
                    className="w-full px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 sm:w-auto"
                    onClick={onGoToRegisterSettings}
                  >
                    Ir a configuración de cajas
                  </button>
                ) : (
                  <button
                    type="button"
                    className="w-full px-3 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 sm:w-auto"
                    onClick={onGoHome}
                  >
                    Ir al inicio
                  </button>
                )}
              </div>
            </>
          ) : (
            !loading && !error && (
              <div className="space-y-3">
                {canShowRegisterless && (
                  <button
                    type="button"
                    onClick={() => onSelectRegisterless?.(registerlessProfile)}
                    className="w-full text-left border rounded-xl p-3 transition sm:p-4 border-amber-200 bg-amber-50/60 hover:border-amber-300"
                  >
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="text-sm font-semibold text-amber-900">Venta sin caja</div>
                        <div className="text-xs text-amber-800">
                          Finalizar suspende la venta con nombre; el pago se completa desde una caja activa.
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-amber-700 bg-white/70 border border-amber-200 rounded-full px-2 py-1">
                        Sin sesión
                      </span>
                    </div>
                  </button>
                )}
                {branchRegisters.map((register) => {
                  const isSelected = String(register.id) === String(selectedId);
                  const status = renderRegisterStatus(register);
                  const categories = resolveRegisterCategories(register);
                  const maxVisible = 6;
                  const visibleCategories = categories.slice(0, maxVisible);
                  const remaining = categories.length - visibleCategories.length;
                  return (
                    <button
                      key={register.id}
                      type="button"
                      onClick={() => setSelectedId(String(register.id))}
                      className={`w-full text-left border rounded-xl p-3 transition sm:p-4 ${
                        isSelected
                          ? 'border-blue-400 ring-1 ring-blue-200 bg-blue-50/40'
                          : 'border-gray-200 hover:border-blue-200 hover:bg-blue-50/20'
                      }`}
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                        <div className="min-w-0">
                          <div className="break-words text-sm font-semibold text-gray-800">
                            {register.name} {register.code ? `(${register.code})` : ''}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {register.maxOpenSales != null ? `Limite ventas abiertas: ${register.maxOpenSales}` : 'Sin limite de ventas abiertas'}
                          </div>
                        </div>
                        <span
                          className={`self-start rounded-full border px-2 py-1 text-xs font-semibold sm:flex-none ${status.className}`}
                        >
                          {status.label}
                        </span>
                      </div>
                      <div className="mt-3">
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                          Categorias habilitadas
                        </div>
                        {categories.length === 0 ? (
                          <div className="text-xs text-gray-500">
                            Sin categorias asignadas.
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {visibleCategories.map((category) => (
                              <span
                                key={category.id}
                                className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700"
                              >
                                {category.name}
                              </span>
                            ))}
                            {remaining > 0 && (
                              <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                                +{remaining} mas
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )
          )}

          {selectedRegister && (
            <div className="mt-4">
              {activeSession ? (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-700 mb-4">
                  {isSessionLocked ? (
                    <>
                      Caja abierta por {sessionOwnerLabel}. Espera a que se cierre para usarla.
                    </>
                  ) : (
                    <>
                      Caja abierta desde {new Date(activeSession.openedAt).toLocaleTimeString()}
                    </>
                  )}
                </div>
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fondo inicial
                  </label>
                  <input
                    className="border border-gray-300 rounded p-2 w-full mb-4 text-base sm:text-sm"
                    type="number"
                    min="0"
                    value={openingCash}
                    onChange={(e) => setOpeningCash(e.target.value)}
                    disabled={openingSession}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {branchRegisters.length > 0 && (
        <div className="flex flex-col-reverse gap-2 mt-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="w-full px-3 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            onClick={onClose}
            disabled={openingSession}
          >
            Cancelar
          </button>
          {activeSession ? (
            !isSessionLocked && (
              <button
                type="button"
                className="w-full px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 sm:w-auto"
                onClick={() => onSelectSession?.(selectedRegister, activeSession, resolvedBranchId)}
              >
                Usar sesion abierta
              </button>
            )
          ) : (
            canOpenSession && (
              <button
                type="button"
                className="w-full px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                onClick={() => onOpenSession?.(selectedRegister, openingCash, resolvedBranchId)}
                disabled={!selectedRegister || openingSession}
                aria-busy={openingSession}
              >
                {openingSession ? 'Abriendo caja...' : 'Abrir caja'}
              </button>
            )
          )}
        </div>
      )}
    </>
  );

  const renderClose = () => (
    <>
      <h2 className="text-lg font-semibold mb-3">{closeTitle}</h2>
      <div className="text-sm text-gray-700 mb-4">
        {selectedRegister?.name || 'Caja'} {selectedRegister?.code ? `(${selectedRegister.code})` : ''}
      </div>
      {closeDescription && (
        <div className="mb-4 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {closeDescription}
        </div>
      )}
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Efectivo contado
      </label>
      <input
        className="border border-gray-300 rounded p-2 w-full mb-4 text-base sm:text-sm"
        type="number"
        min="0"
        value={closingCash}
        onChange={(e) => setClosingCash(e.target.value)}
        disabled={closingSession}
      />
      <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-700 mb-4">
        <div className="flex items-center justify-between gap-3">
          <span>Efectivo esperado</span>
          <span className="text-right">
            {expectedCashLoading
              ? <InlineLoading label="Calculando" />
              : expectedCashValue === null
                ? '-'
                : formatCurrency(expectedCashValue)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3 mt-1">
          <span>Diferencia (contado - esperado)</span>
          <span className={`text-right ${differenceClassName}`}>
            {cashDifference === null ? '-' : formatCurrency(cashDifference)}
          </span>
        </div>
        {expectedCashError && (
          <div className="text-xs text-red-600 mt-2">{expectedCashError}</div>
        )}
      </div>
      <div
        className={`border rounded p-3 text-sm mb-4 ${
          hasOpenSales ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'
        }`}
      >
        <div className="font-medium text-gray-800 mb-2">Ventas abiertas en esta caja</div>
        {openSalesLoading ? (
          <div className="text-gray-600">
            <InlineLoading label="Cargando ventas abiertas" />
          </div>
        ) : openSalesError ? (
          <div className="text-red-600">{openSalesError}</div>
        ) : hasOpenSales ? (
          <>
            <div className="text-xs text-red-700 mb-2">
              No puedes cerrar la caja mientras existan ventas abiertas.
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {openSales.map((sale) => {
                const clientName = sale?.Client?.name || 'Venta Detalle';
                const totalLabel = Number.isFinite(Number(sale?.totalAmount))
                  ? formatCurrency(sale.totalAmount)
                  : '';
                return (
                  <div
                    key={sale.id}
                    className="flex flex-col gap-2 border border-red-100 bg-white rounded p-2 sm:flex-row sm:items-start sm:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-gray-800">Venta #{sale.id}</div>
                      <div className="break-words text-xs text-gray-600">
                        {clientName}{totalLabel ? ` · ${totalLabel}` : ''}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-col sm:gap-1">
                      {canOpenSale && (
                        <button
                          type="button"
                          className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700"
                          onClick={() => onOpenSale?.(sale)}
                        >
                          Continuar
                        </button>
                      )}
                      {canCancelSale && (
                        <button
                          type="button"
                          className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700"
                          onClick={() => onCancelSale?.(sale)}
                        >
                          Anular
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="text-gray-600">No hay ventas abiertas.</div>
        )}
      </div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {closeReasonLabel || `Motivo de diferencia ${requiresReason ? '(obligatorio)' : '(si aplica)'}`}
      </label>
      <textarea
        className="border border-gray-300 rounded p-2 w-full mb-4 text-base sm:text-sm"
        rows="3"
        value={closeReason}
        onChange={(e) => setCloseReason(e.target.value)}
        required={requiresReason}
        disabled={closingSession}
      />
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          className="w-full px-3 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 sm:w-auto"
          onClick={onClose}
          disabled={closingSession}
        >
          Cancelar
        </button>
        {canCloseSession && (
          <button
            type="button"
            className="w-full px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed sm:w-auto"
            onClick={() => onCloseSession?.(closingCash, closeReason)}
            disabled={closingSession || closingCashValue === null || missingReason || hasOpenSales}
            aria-busy={closingSession}
          >
            {closingSession ? 'Cerrando caja...' : 'Cerrar caja'}
          </button>
        )}
      </div>
    </>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black bg-opacity-50 px-2 py-3 sm:items-center sm:px-4 sm:py-6"
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className={`max-h-[calc(100dvh-1.5rem)] w-full overflow-y-auto rounded-lg bg-white p-4 shadow-lg sm:max-h-[calc(100dvh-3rem)] sm:p-6 ${
          mode === 'open' ? 'max-w-5xl' : 'max-w-md'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {mode === 'close' ? renderClose() : renderOpen()}
      </div>
    </div>
  );
};

export default PosSessionModal;
